import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, X, Check, UsersThree, Trash, SealCheck } from "@phosphor-icons/react";

export default function Friends() {
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState({ incoming: [], outgoing: [] });
  const [attestations, setAttestations] = useState({ incoming: [], outgoing: [] });
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [f, r, a] = await Promise.all([
      api.get("/friends"),
      api.get("/friends/requests"),
      api.get("/attestations"),
    ]);
    setFriends(f.data); setRequests(r.data); setAttestations(a.data);
  };
  useEffect(() => { load(); }, []);

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/friends/request", { email });
      if (data.auto_accepted) toast.success("Already had a pending request — you're friends now!");
      else toast.success("Request sent");
      setEmail("");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const accept = async (id) => { await api.post(`/friends/requests/${id}/accept`); toast.success("Friend added"); load(); };
  const decline = async (id) => { await api.post(`/friends/requests/${id}/decline`); load(); };
  const removeFriend = async (id) => {
    if (!window.confirm("Remove this friend?")) return;
    await api.delete(`/friends/${id}`); toast.success("Friend removed"); load();
  };
  const acceptAttest = async (aid) => { await api.post(`/attestations/${aid}/accept`); toast.success("Round attested"); load(); };
  const declineAttest = async (aid) => { await api.post(`/attestations/${aid}/decline`); load(); };

  return (
    <div className="space-y-6">
      <div className="rise">
        <span className="label-eyebrow">Friends</span>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">Your golf crew.</h1>
        <p className="text-[#0F2537]/70 mt-2">Add friends by email to filter the leaderboard.</p>
      </div>

      <form onSubmit={send} className="card-default p-5 flex flex-col sm:flex-row gap-3" data-testid="add-friend-form">
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="friend@example.com" data-testid="friend-email-input"
          className="flex-1 px-4 py-3 rounded-xl border border-[#E2E0D8] bg-white focus:outline-none focus:border-[#0F2537]" />
        <button type="submit" disabled={busy} data-testid="send-friend-request-btn"
          className="px-5 py-3 rounded-xl bg-[#E05D3A] text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60">
          <UserPlus weight="bold" /> {busy ? "Sending…" : "Send request"}
        </button>
      </form>

      {attestations.incoming.length > 0 && (
        <div className="card-default p-5" data-testid="attest-inbox">
          <span className="label-eyebrow">Attestation requests</span>
          <p className="text-xs text-[#0F2537]/60 mt-1">Review each scorecard before you sign — your signature attests it's accurate.</p>
          <ul className="mt-3 divide-y divide-[#E2E0D8]">
            {attestations.incoming.map((a) => (
              <li key={a.id} className="py-4" data-testid={`attest-incoming-${a.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-semibold inline-flex items-center gap-1">
                      <SealCheck size={14} weight="fill" className="text-[#1E5631]" />
                      {a.requester_name}
                    </div>
                    <div className="text-xs text-[#0F2537]/60">{a.course_name} · {new Date(a.created_at).toLocaleDateString()}</div>
                    {a.round && (
                      <div className="mt-1 text-xs text-[#0F2537]/70 font-semibold">
                        {a.round.total_strokes} ({a.round.total_strokes - a.round.total_par >= 0 ? "+" : ""}{a.round.total_strokes - a.round.total_par}) · {a.round.tees} · diff {a.round.differential?.toFixed?.(1) ?? a.round.differential}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => acceptAttest(a.id)} data-testid={`attest-accept-${a.id}`} className="px-3 py-1.5 rounded-lg bg-[#1E5631] text-white text-sm font-bold inline-flex items-center gap-1"><Check size={14} /> Sign</button>
                    <button onClick={() => declineAttest(a.id)} data-testid={`attest-decline-${a.id}`} className="px-3 py-1.5 rounded-lg border border-[#E2E0D8] text-sm font-bold inline-flex items-center gap-1"><X size={14} /> Decline</button>
                  </div>
                </div>
                {a.round?.holes && (
                  <details className="mt-2 text-xs">
                    <summary className="cursor-pointer text-[#0F2537]/70 hover:text-[#E05D3A] font-semibold inline-block" data-testid={`attest-preview-toggle-${a.id}`}>
                      View hole-by-hole →
                    </summary>
                    <div className="mt-2 grid grid-cols-9 gap-1 text-center">
                      {a.round.holes.map((h) => (
                        <div key={h.number} className="rounded-md border border-[#E2E0D8] p-1">
                          <div className="text-[10px] opacity-60">H{h.number}</div>
                          <div className="font-bold">{h.strokes}</div>
                          <div className="text-[9px] opacity-50">par {h.par}</div>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {requests.incoming.length > 0 && (
        <div className="card-default p-5">
          <span className="label-eyebrow">Incoming requests</span>
          <ul className="mt-3 divide-y divide-[#E2E0D8]">
            {requests.incoming.map((r) => (
              <li key={r.id} className="flex items-center justify-between py-3" data-testid={`incoming-${r.id}`}>
                <div>
                  <div className="font-semibold">{r.from_name}</div>
                  <div className="text-xs text-[#0F2537]/60">{new Date(r.created_at).toLocaleDateString()}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => accept(r.id)} data-testid={`accept-${r.id}`} className="px-3 py-1.5 rounded-lg bg-[#1E5631] text-white text-sm font-bold inline-flex items-center gap-1"><Check size={14} /> Accept</button>
                  <button onClick={() => decline(r.id)} data-testid={`decline-${r.id}`} className="px-3 py-1.5 rounded-lg border border-[#E2E0D8] text-sm font-bold inline-flex items-center gap-1"><X size={14} /> Decline</button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {requests.outgoing.length > 0 && (
        <div className="card-default p-5">
          <span className="label-eyebrow">Pending sent</span>
          <ul className="mt-3 divide-y divide-[#E2E0D8]">
            {requests.outgoing.map((r) => (
              <li key={r.id} className="flex items-center justify-between py-3 text-sm">
                <span>{r.to_name}</span>
                <span className="text-[#0F2537]/60 text-xs">Awaiting…</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="card-default p-5">
        <span className="label-eyebrow">My friends</span>
        {friends.length === 0 ? (
          <div className="py-10 text-center text-[#0F2537]/60">
            <UsersThree size={32} weight="duotone" className="mx-auto text-[#E05D3A]" />
            <p className="mt-2">No friends yet. Send a request to get started.</p>
          </div>
        ) : (
          <ul className="mt-3 divide-y divide-[#E2E0D8]" data-testid="friends-list">
            {friends.map((f) => (
              <li key={f.id} className="flex items-center justify-between py-3" data-testid={`friend-${f.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#0F2537] text-white flex items-center justify-center font-bold">
                    {f.name?.slice(0, 1).toUpperCase()}
                  </div>
                  <div>
                    <div className="font-semibold">{f.name}</div>
                    <div className="text-xs text-[#0F2537]/60">{f.email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="label-eyebrow">Hcp</div>
                    <div className="font-heading font-bold">{f.handicap_index ?? "—"}</div>
                  </div>
                  <button onClick={() => removeFriend(f.id)} data-testid={`remove-${f.id}`} className="p-2 text-[#0F2537]/60 hover:text-red-600"><Trash size={16} /></button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
